import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    Tag,
    message,
    Popconfirm,
    Switch,
    Tree,
    Alert,
    Typography,
} from 'antd'
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    EyeOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import {
    FilterActions,
    FilterBar,
    FilterField,
} from '../../components/common/FilterBar'
import {
    PaginatedListCard,
    PaginatedListContainer,
    PaginatedListToolbar,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
    renderPaginatedListText,
} from '../../components/common/PaginatedListTable'
import { PageLayout } from '../../components/layout'
import { useAuth } from '../../hooks/useAuth'
import { usePermissions } from '../../hooks/usePermissions'
import { getLocalizedApiErrorMessage } from '../../utils/apiErrorI18n'
import { ROLE_PERMISSION_TREE_SECTIONS } from '../../utils/permissions'
import {
    getBuiltinRoleNameCandidates,
    getRoleBuiltinDescription,
    getRoleDisplayName,
} from '../../utils/roleDisplay'
import { normalizeSearchText } from '../../utils/searchText'
import {
    getRoles,
    getRole,
    createRole,
    updateRole,
    deleteRole,
    getPermissionsGrouped,
    Permission,
    Role,
    RoleWithUserCount,
    PermissionGrouped,
    CreateRoleRequest,
} from '../../services/rbacService'

const HIDDEN_PERMISSION_RESOURCES = new Set(['auth', 'ai', 'detection'])
const DROPPED_HIDDEN_PERMISSION_NAMES = new Set([
    'project:manage',
    'home:dashboard',
    'stat:read',
    'stat:export',
    'statistics:export',
    'device:distribution',
])
const VISIBLE_SYSTEM_PERMISSION_NAMES = new Set([
    'system:config',
    'system:config:read',
    'system:config:update',
    'system:log',
    'system:logs',
])

const { Text } = Typography
const ROLE_NAME_MIN_LENGTH = 2
const ROLE_NAME_MAX_LENGTH = 20
const ROLE_NAME_PATTERN = /^[\u4e00-\u9fffA-Za-z0-9_]+$/
const ROLE_DESCRIPTION_MAX_LENGTH = 255
const roleModalBodyStyle: React.CSSProperties = {
    maxHeight: 'calc(100vh - 180px)',
    overflow: 'hidden',
}
const rolePermissionSectionStyle: React.CSSProperties = {
    marginTop: 16,
}
const rolePermissionScrollPanelStyle: React.CSSProperties = {
    maxHeight: 'min(420px, calc(100vh - 420px))',
    overflowY: 'auto',
    paddingRight: 8,
}

interface PermissionViewPageDefinition {
    key: string
    title: string
    description?: string
    capabilities: PermissionViewCapabilityDefinition[]
}

interface PermissionViewSectionDefinition {
    key: string
    title: string
    pages: PermissionViewPageDefinition[]
}

interface PermissionViewCapabilityDefinition {
    key: string
    label: string
    permissionNames: string[]
}

interface PermissionViewCapability {
    key: string
    label: string
    permissions: Permission[]
}

interface PermissionViewPage {
    key: string
    title: string
    description?: string
    capabilities: PermissionViewCapability[]
}

interface PermissionViewSection {
    key: string
    title: string
    pages: PermissionViewPage[]
}

const getCheckedCapabilityKeys = (
    checked:
        | React.Key[]
        | {
            checked: React.Key[]
            halfChecked: React.Key[]
        }
): string[] => {
    const keys = Array.isArray(checked) ? checked : checked.checked
    return keys
        .map((key) => String(key))
        .filter((key) => !key.startsWith('section:') && !key.startsWith('page:'))
}

const isVisiblePermission = (permission: Permission): boolean =>
    !HIDDEN_PERMISSION_RESOURCES.has(permission.resource) &&
    (permission.resource !== 'system' ||
        VISIBLE_SYSTEM_PERMISSION_NAMES.has(permission.name))

const isVisiblePermissionGroup = (group: PermissionGrouped): boolean =>
    !HIDDEN_PERMISSION_RESOURCES.has(group.resource) &&
    group.permissions.some(isVisiblePermission)

const getAssignablePermissionIdSet = (permissions: PermissionGrouped[]): Set<number> =>
    new Set(
        permissions
            .filter(isVisiblePermissionGroup)
            .flatMap((group) =>
                group.permissions
                    .filter(isVisiblePermission)
                    .map((permission) => permission.id)
            )
    )

const getVisiblePermissions = (permissions: PermissionGrouped[]): Permission[] =>
    permissions
        .filter(isVisiblePermissionGroup)
        .flatMap((group) => group.permissions.filter(isVisiblePermission))

const isProjectRole = (role: Pick<RoleWithUserCount, 'scope_type'>): boolean =>
    role.scope_type === 'project'

const getRoleListGroupOrder = (
    role: Pick<RoleWithUserCount, 'is_system' | 'scope_type'>
): number => {
    if (role.is_system) {
        return 0
    }
    return isProjectRole(role) ? 2 : 1
}

const isOwnRole = (
    role: Pick<RoleWithUserCount, 'created_by'>,
    currentUserId?: string | number | null
): boolean => {
    if (role.created_by == null || currentUserId == null) {
        return false
    }
    return String(role.created_by) === String(currentUserId)
}

const RoleManagement: React.FC = () => {
    const { t } = useTranslation('admin')
    const { user: currentLoginUser, refreshUser } = useAuth()
    const formatApiError = useCallback(
        (error: unknown, fallback: string) =>
            getLocalizedApiErrorMessage(error, fallback, t),
        [t]
    )
    const {
        canCreateRoles,
        canDeleteRoles,
        canUpdateRoles,
        canViewRolePermissions,
    } = usePermissions()

    const getPermissionViewDefinition = useCallback(
        (): PermissionViewSectionDefinition[] =>
            ROLE_PERMISSION_TREE_SECTIONS.map((section) => ({
                key: section.key,
                title: t(section.titleKey, { ns: 'navigation' }),
                pages: section.pages.map((page) => ({
                    key: page.key,
                    title: t(page.titleKey, { ns: 'navigation' }),
                    description: page.descriptionKey
                        ? t(page.descriptionKey)
                        : undefined,
                    capabilities: page.capabilities.map((capability) => ({
                        key: capability.key,
                        label: t(capability.labelKey, {
                            defaultValue: capability.defaultLabel,
                        }),
                        permissionNames: [...capability.permissionNames],
                    })),
                })),
            })),
        [t]
    )

    const [roles, setRoles] = useState<RoleWithUserCount[]>([])
    const [permissions, setPermissions] = useState<PermissionGrouped[]>([])
    const [loading, setLoading] = useState(false)
    const [roleSubmitting, setRoleSubmitting] = useState(false)
    const roleSubmittingRef = useRef(false)
    const [roleModalVisible, setRoleModalVisible] = useState(false)
    const [permissionModalVisible, setPermissionModalVisible] = useState(false)
    const [currentRole, setCurrentRole] = useState<Role | null>(null)
    const [currentCapabilityKeys, setCurrentCapabilityKeys] = useState<string[]>([])
    const [permissionTreeRenderId, setPermissionTreeRenderId] = useState(0)
    const [readonlyPermissionTreeRenderId, setReadonlyPermissionTreeRenderId] = useState(0)
    const [hiddenPermissionIds, setHiddenPermissionIds] = useState<number[]>([])
    const [permissionError, setPermissionError] = useState<string | null>(null)
    const [total, setTotal] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [roleNameKeyword, setRoleNameKeyword] = useState('')
    const [appliedRoleNameKeyword, setAppliedRoleNameKeyword] = useState('')
    const [openDeleteRoleId, setOpenDeleteRoleId] = useState<number | null>(null)
    const [form] = Form.useForm()
    const [permissionForm] = Form.useForm()

    const createRoleDisabled = !canCreateRoles
    const assignablePermissionIds = useMemo(
        () => getAssignablePermissionIdSet(permissions),
        [permissions]
    )
    const visiblePermissions = useMemo(
        () => getVisiblePermissions(permissions),
        [permissions]
    )
    const permissionViewSections = useMemo((): PermissionViewSection[] => {
        const permissionByName = new Map(
            visiblePermissions.map((permission) => [permission.name, permission])
        )

        return getPermissionViewDefinition()
            .map((section) => ({
                key: section.key,
                title: section.title,
                pages: section.pages
                    .map((page) => ({
                        key: page.key,
                        title: page.title,
                        description: page.description,
                        capabilities: page.capabilities
                            .map((capability) => ({
                                key: capability.key,
                                label: capability.label,
                                permissions: capability.permissionNames
                                    .map((name) => permissionByName.get(name))
                                    .filter((permission): permission is Permission => !!permission),
                            }))
                            .filter((capability) => capability.permissions.length > 0),
                    }))
                    .filter((page) => page.capabilities.length > 0),
            }))
            .filter((section) => section.pages.length > 0)
    }, [getPermissionViewDefinition, visiblePermissions])
    const treePermissionIds = useMemo(
        () =>
            new Set(
                permissionViewSections.flatMap((section) =>
                    section.pages.flatMap((page) =>
                        page.capabilities.flatMap((capability) =>
                            capability.permissions.map((permission) => String(permission.id))
                        )
                    )
                )
            ),
        [permissionViewSections]
    )
    const capabilityPermissionIdMap = useMemo(
        () =>
            new Map(
                permissionViewSections.flatMap((section) =>
                    section.pages.flatMap((page) =>
                        page.capabilities.map((capability) => [
                            capability.key,
                            capability.permissions.map((permission) => permission.id),
                        ] as const)
                    )
                )
            ),
        [permissionViewSections]
    )
    const getCapabilityKeysForPermissions = useCallback(
        (rolePermissions: Permission[]): string[] => {
            const rolePermissionIds = new Set(rolePermissions.map((permission) => permission.id))
            return permissionViewSections.flatMap((section) =>
                section.pages.flatMap((page) =>
                    page.capabilities
                        .filter((capability) =>
                            capability.permissions.some((permission) =>
                                rolePermissionIds.has(permission.id)
                            )
                        )
                        .map((capability) => capability.key)
                )
            )
        },
        [permissionViewSections]
    )
    const expandCapabilityKeysToPermissionIds = useCallback(
        (capabilityKeys: string[]): number[] => {
            const permissionIds = new Set<number>()
            capabilityKeys.forEach((key) => {
                capabilityPermissionIdMap.get(key)?.forEach((id) => {
                    permissionIds.add(id)
                })
            })
            return [...permissionIds]
        },
        [capabilityPermissionIdMap]
    )
    const sortedRoles = useMemo(
        () => [...roles].sort((left, right) => {
            const leftGroupOrder = getRoleListGroupOrder(left)
            const rightGroupOrder = getRoleListGroupOrder(right)
            if (leftGroupOrder === rightGroupOrder) {
                return 0
            }
            return leftGroupOrder - rightGroupOrder
        }),
        [roles]
    )
    const isReadonlyProjectRole = useCallback(
        (role: RoleWithUserCount) =>
            isProjectRole(role) && !isOwnRole(role, currentLoginUser?.id),
        [currentLoginUser?.id]
    )

    const resetPermissionSelectionState = useCallback((capabilityKeys: string[] = []) => {
        setCurrentCapabilityKeys(capabilityKeys)
        setHiddenPermissionIds([])
        setPermissionError(null)
        permissionForm.resetFields()
        permissionForm.setFieldsValue({
            permission_keys: capabilityKeys,
        })
        permissionForm.setFields([
            {
                name: 'permission_keys',
                errors: [],
            },
        ])
    }, [permissionForm])

    const resetPermissionTreeExpansion = useCallback((readOnly = false) => {
        if (readOnly) {
            setReadonlyPermissionTreeRenderId((renderId) => renderId + 1)
            return
        }

        setPermissionTreeRenderId((renderId) => renderId + 1)
    }, [])

    const closeRoleModal = useCallback(() => {
        setRoleModalVisible(false)
        setCurrentRole(null)
        resetPermissionSelectionState()
        form.resetFields()
    }, [form, resetPermissionSelectionState])

    const closePermissionModal = useCallback(() => {
        setPermissionModalVisible(false)
        setCurrentRole(null)
        resetPermissionSelectionState()
    }, [resetPermissionSelectionState])

    // 加载角色列表
    const loadRoles = useCallback(async (page = 1, size = 10, name = '') => {
        setLoading(true)
        try {
            const normalizedName = name.trim()
            const roleNameCandidates = getBuiltinRoleNameCandidates(normalizedName, t)
            const data = await getRoles({
                skip: (page - 1) * size,
                limit: size,
                name: normalizedName || undefined,
                roleNames: roleNameCandidates.length > 0 ? roleNameCandidates : undefined,
            })
            setRoles(data.roles || [])
            setTotal(data.total || 0)
        } catch (error: unknown) {
            message.error(formatApiError(error, t('role.messages.loadFailed')))
        } finally {
            setLoading(false)
        }
    }, [formatApiError, t])

    // 加载权限列表（按资源分组）
    const loadPermissions = useCallback(async () => {
        try {
            const data = await getPermissionsGrouped()
            setPermissions(data)
        } catch (error) {
            console.error('Failed to load permissions:', error)
        }
    }, [])

    useEffect(() => {
        void loadRoles()
        if (canViewRolePermissions || canCreateRoles || canUpdateRoles) {
            void loadPermissions()
        }
    }, [canCreateRoles, canUpdateRoles, canViewRolePermissions, loadPermissions, loadRoles])

    // 打开创建角色对话框
    const handleCreate = () => {
        if (createRoleDisabled) {
            return
        }
        setCurrentRole(null)
        form.resetFields()
        resetPermissionSelectionState()
        resetPermissionTreeExpansion()
        setRoleModalVisible(true)
    }

    // 打开编辑角色对话框
    const handleSearchFilters = () => {
        const nextRoleName = normalizeSearchText(roleNameKeyword)
        setAppliedRoleNameKeyword(nextRoleName)
        setCurrentPage(1)
        void loadRoles(1, pageSize, nextRoleName)
    }

    const handleResetFilters = () => {
        setRoleNameKeyword('')
        setAppliedRoleNameKeyword('')
        setCurrentPage(1)
        void loadRoles(1, pageSize, '')
    }

    const handleEdit = async (role: RoleWithUserCount) => {
        if (role.is_system || !canUpdateRoles) {
            return
        }
        try {
            const fullRole = await getRole(role.id)
            setCurrentRole(fullRole)
            const visiblePermissionsForTree = fullRole.permissions
                .filter((perm) =>
                    isVisiblePermission(perm) &&
                    assignablePermissionIds.has(perm.id) &&
                    treePermissionIds.has(String(perm.id))
                )
            const visibleCapabilityKeys = getCapabilityKeysForPermissions(visiblePermissionsForTree)
            const nextHiddenPermissionIds = fullRole.permissions
                .filter((perm) =>
                    !DROPPED_HIDDEN_PERMISSION_NAMES.has(perm.name) &&
                    (
                        !isVisiblePermission(perm) ||
                        !assignablePermissionIds.has(perm.id) ||
                        !treePermissionIds.has(String(perm.id))
                    )
                )
                .map((perm) => perm.id)
            form.setFieldsValue({
                name: fullRole.name,
                description: fullRole.description,
                is_active: fullRole.is_active,
            })
            setCurrentCapabilityKeys(visibleCapabilityKeys)
            setHiddenPermissionIds(nextHiddenPermissionIds)
            permissionForm.setFieldsValue({
                permission_keys: visibleCapabilityKeys,
            })
            permissionForm.setFields([
                {
                    name: 'permission_keys',
                    errors: [],
                },
            ])
            setPermissionError(null)
            resetPermissionTreeExpansion()
            setRoleModalVisible(true)
        } catch (error: unknown) {
            message.error(formatApiError(error, t('role.messages.loadFailed')))
        }
    }

    // 打开权限分配对话框
    const handleViewPermissions = async (role: RoleWithUserCount) => {
        if (!canViewRolePermissions) {
            return
        }
        try {
            const fullRole = await getRole(role.id)
            setCurrentRole(fullRole)
            const visiblePermissionsForTree = fullRole.permissions
                .filter((perm) =>
                    isVisiblePermission(perm) &&
                    assignablePermissionIds.has(perm.id) &&
                    treePermissionIds.has(String(perm.id))
                )
            const visibleCapabilityKeys = getCapabilityKeysForPermissions(visiblePermissionsForTree)
            const nextHiddenPermissionIds = fullRole.permissions
                .filter((perm) =>
                    !DROPPED_HIDDEN_PERMISSION_NAMES.has(perm.name) &&
                    (
                        !isVisiblePermission(perm) ||
                        !assignablePermissionIds.has(perm.id) ||
                        !treePermissionIds.has(String(perm.id))
                    )
                )
                .map((perm) => perm.id)
            setCurrentCapabilityKeys(visibleCapabilityKeys)
            setHiddenPermissionIds(nextHiddenPermissionIds)
            permissionForm.setFieldsValue({
                permission_keys: visibleCapabilityKeys,
            })
            permissionForm.setFields([
                {
                    name: 'permission_keys',
                    errors: [],
                },
            ])
            setPermissionError(null)
            resetPermissionTreeExpansion(true)
            setPermissionModalVisible(true)
        } catch (error: unknown) {
            message.error(formatApiError(error, t('role.messages.loadFailed')))
        }
    }

    const getValidatedPermissionIds = (): number[] | null => {
        const selectedCapabilityKeys =
            permissionForm.getFieldValue('permission_keys') || currentCapabilityKeys
        const visiblePermissionIds = expandCapabilityKeysToPermissionIds(selectedCapabilityKeys)
        const permissionIds = [...visiblePermissionIds, ...hiddenPermissionIds]

        if (permissionIds.length === 0) {
            setPermissionError(t('role.form.permissionsRequired'))
            return null
        }

        setPermissionError(null)
        return [...new Set(permissionIds)]
    }

    // 提交角色表单
    const handleSubmitRole = async () => {
        if (roleSubmittingRef.current) {
            return
        }

        roleSubmittingRef.current = true
        setRoleSubmitting(true)
        try {
            const values = await form.validateFields()
            if (currentRole) {
                if (!canUpdateRoles) {
                    return
                }
                // 更新角色
                const permissionIds = getValidatedPermissionIds()
                if (permissionIds === null) {
                    return
                }
                await updateRole(currentRole.id, {
                    name: values.name,
                    description: values.description,
                    is_active: values.is_active,
                    permission_ids: permissionIds,
                })
                await refreshUser()
                message.success(t('role.messages.updateSuccess'))
            } else {
                if (!canCreateRoles) {
                    return
                }
                // 创建角色
                const permissionIds = getValidatedPermissionIds()
                if (permissionIds === null) {
                    return
                }
                const roleData: CreateRoleRequest = {
                    name: values.name,
                    description: values.description,
                    permission_ids: permissionIds,
                }
                await createRole(roleData)
                message.success(t('role.messages.createSuccess'))
            }
            closeRoleModal()
            void loadRoles(currentPage, pageSize, appliedRoleNameKeyword)
        } catch (error: unknown) {
            message.error(formatApiError(error, t('role.messages.operationFailed')))
        } finally {
            roleSubmittingRef.current = false
            setRoleSubmitting(false)
        }
    }

    // 提交权限分配
    // 删除角色
    const handleDelete = async (role: RoleWithUserCount) => {
        if (!canDeleteRoles) {
            return
        }
        if (role.user_count > 0) {
            message.error(t('role.messages.roleAssignedToUsers'))
            return
        }
        try {
            await deleteRole(role.id)
            message.success(t('role.messages.deleteSuccess'))
            const nextTotal = Math.max(total - 1, 0)
            const maxPage = Math.max(1, Math.ceil(nextTotal / pageSize))
            const nextPage = Math.min(currentPage, maxPage)
            if (nextPage !== currentPage) {
                setCurrentPage(nextPage)
            }
            void loadRoles(nextPage, pageSize, appliedRoleNameKeyword)
        } catch (error: unknown) {
            message.error(formatApiError(error, t('role.messages.deleteFailed')))
        }
    }

    // 将权限数据转换为 Tree 组件所需格式 (key必须是字符串)
    const getPermissionTreeData = (ownedOnly = false) => {
        const ownedCapabilityKeys = new Set(currentCapabilityKeys)

        return permissionViewSections.map((section) => ({
            key: `section:${section.key}`,
            title: <Text strong>{section.title}</Text>,
            children: section.pages
                .map((page) => ({
                    key: `page:${page.key}`,
                    title: (
                        <Space direction="vertical" size={0}>
                            <Text>{page.title}</Text>
                            {page.description && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {page.description}
                                </Text>
                            )}
                        </Space>
                    ),
                    children: page.capabilities
                        .filter((capability) =>
                            !ownedOnly || ownedCapabilityKeys.has(capability.key)
                        )
                        .map((capability) => ({
                            key: capability.key,
                            title: capability.label,
                        })),
                }))
                .filter((page) => !ownedOnly || page.children.length > 0),
        })).filter((section) => !ownedOnly || section.children.length > 0)
    }

    const getPermissionTreeExpandedKeys = (ownedOnly = false): React.Key[] => {
        const ownedCapabilityKeys = new Set(currentCapabilityKeys)

        return permissionViewSections.flatMap((section) => {
            const pageKeys = section.pages
                .filter((page) =>
                    !ownedOnly ||
                    page.capabilities.some((capability) =>
                        ownedCapabilityKeys.has(capability.key)
                    )
                )
                .map((page) => `page:${page.key}`)

            if (ownedOnly && pageKeys.length === 0) {
                return []
            }

            return [`section:${section.key}`, ...pageKeys]
        })
    }

    const renderPermissionTree = (readOnly = false) => {
        const permissionTreeKey = readOnly
            ? `readonly-${readonlyPermissionTreeRenderId}`
            : `editable-${permissionTreeRenderId}`

        return (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Alert
                    type="info"
                    showIcon
                    message={t('role.permissionView.hint')}
                />
                <Form form={permissionForm} layout="vertical">
                    <Form.Item
                        name="permission_keys"
                        initialValue={currentCapabilityKeys}
                    >
                        <Tree
                            key={permissionTreeKey}
                            checkable
                            defaultExpandedKeys={getPermissionTreeExpandedKeys(readOnly)}
                            treeData={getPermissionTreeData(readOnly)}
                            onCheck={(checked) => {
                                if (readOnly) {
                                    return
                                }
                                const capabilityKeys = getCheckedCapabilityKeys(checked)
                                permissionForm.setFieldsValue({ permission_keys: capabilityKeys })
                                permissionForm.setFields([
                                    {
                                        name: 'permission_keys',
                                        errors: [],
                                    },
                                ])
                                setPermissionError(null)
                                setCurrentCapabilityKeys(capabilityKeys)
                            }}
                            checkedKeys={currentCapabilityKeys}
                        />
                    </Form.Item>
                </Form>
            </Space>
        )
    }

    // 表格列定义
    const columns = [
        {
            title: t('role.columns.name'),
            dataIndex: 'name',
            key: 'name',
            width: 280,
            ellipsis: true,
            render: (name: string, record: RoleWithUserCount) => (
                <Space size={4} style={{ maxWidth: '100%' }}>
                    {renderPaginatedListText(getRoleDisplayName(name, t))}
                    {record.is_system && <Tag color="orange" style={{ marginLeft: 8 }}>{t('role.labels.system')}</Tag>}
                    {isProjectRole(record) && (
                        <Tag color="blue" style={{ marginLeft: 8 }}>
                            {t('role.labels.projectRole')}
                        </Tag>
                    )}
                </Space>
            ),
        },
        {
            title: t('role.columns.description'),
            dataIndex: 'description',
            key: 'description',
            width: 360,
            ellipsis: true,
            render: (description: string, record: RoleWithUserCount) =>
                renderPaginatedListText(
                    getRoleBuiltinDescription(record.name, t) ?? description
                ),
        },
        {
            title: t('role.columns.status'),
            dataIndex: 'is_active',
            key: 'is_active',
            width: 120,
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? t('role.status.active') : t('role.status.inactive')}
                </Tag>
            ),
        },
        {
            title: t('role.columns.actions'),
            key: 'action',
            width: 320,
            fixed: 'right' as const,
            render: (_: unknown, record: RoleWithUserCount) => {
                const readonlyProjectRole = isReadonlyProjectRole(record)
                return (
                    <Space size="small">
                        {canViewRolePermissions && (
                            <Button
                                type="link"
                                icon={<EyeOutlined />}
                                onClick={() => handleViewPermissions(record)}
                            >
                                {t('role.actions.viewPermissions')}
                            </Button>
                        )}
                        {!readonlyProjectRole && !record.is_system && (
                            <>
                                {canUpdateRoles && (
                                    <Button
                                        type="link"
                                        icon={<EditOutlined />}
                                        onClick={() => handleEdit(record)}
                                    >
                                        {t('role.actions.edit')}
                                    </Button>
                                )}
                                {canDeleteRoles && (
                                    <Popconfirm
                                        title={t('role.confirmDelete')}
                                        open={openDeleteRoleId === record.id}
                                        onOpenChange={(open) => {
                                            if (open) {
                                                setOpenDeleteRoleId(record.id)
                                            }
                                        }}
                                        onCancel={() => setOpenDeleteRoleId(null)}
                                        onConfirm={() => {
                                            setOpenDeleteRoleId(null)
                                            handleDelete(record)
                                        }}
                                        okText={t('common:buttons.ok')}
                                        cancelText={t('common:buttons.cancel')}
                                    >
                                        <Button type="link" danger icon={<DeleteOutlined />}>
                                            {t('role.actions.delete')}
                                        </Button>
                                    </Popconfirm>
                                )}
                            </>
                        )}
                    </Space>
                )
            },
        },
    ]

    return (
        <PageLayout
            title={t('role.title')}
            breadcrumb={[
                { title: t('navigation:breadcrumb.systemManagement') },
                { title: t('navigation:breadcrumb.roleManagement') },
            ]}
        >
            <FilterBar>
                <FilterField label={t('role.filters.nameLabel')}>
                    <Input
                        allowClear
                        value={roleNameKeyword}
                        placeholder={t('role.filters.namePlaceholder')}
                        onChange={(event) => setRoleNameKeyword(event.target.value)}
                        onPressEnter={handleSearchFilters}
                    />
                </FilterField>
                <FilterActions>
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={handleSearchFilters}
                    >
                        {t('role.filters.search')}
                    </Button>
                    <Button onClick={handleResetFilters}>
                        {t('role.filters.reset')}
                    </Button>
                </FilterActions>
            </FilterBar>

            <PaginatedListContainer>
                {canCreateRoles && (
                    <PaginatedListToolbar>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleCreate}
                            disabled={createRoleDisabled}
                        >
                            {t('common:buttons.add', { defaultValue: '添加' })}
                        </Button>
                    </PaginatedListToolbar>
                )}

                <PaginatedListCard>
                    <Table
                        columns={columns}
                        dataSource={sortedRoles}
                        rowKey="id"
                        loading={loading}
                        size={PAGINATED_LIST_TABLE_SIZE}
                        tableLayout="fixed"
                        scroll={{ x: 1080 }}
                        pagination={buildPaginatedListPagination({
                            current: currentPage,
                            pageSize,
                            total,
                            showTotal: (count) =>
                                t('role.totalRoles', { total: count }),
                            onChange: (page, nextPageSize) => {
                                const resolvedPageSize =
                                    nextPageSize ?? pageSize
                                const nextPage =
                                    resolvedPageSize !== pageSize ? 1 : page
                                setPageSize(resolvedPageSize)
                                setCurrentPage(nextPage)
                                void loadRoles(
                                    nextPage,
                                    resolvedPageSize,
                                    appliedRoleNameKeyword
                                )
                            },
                        })}
                    />
                </PaginatedListCard>
            </PaginatedListContainer>

            {/* 角色创建/编辑对话框 */}
            <Modal
                title={currentRole ? t('role.modal.editTitle') : t('role.modal.createTitle')}
                open={roleModalVisible}
                onOk={handleSubmitRole}
                onCancel={closeRoleModal}
                okText={t('common:buttons.save')}
                cancelText={t('common:buttons.cancel')}
                confirmLoading={roleSubmitting}
                width={700}
                maskClosable={false}
                styles={{ body: roleModalBodyStyle }}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label={t('role.form.name')}
                        name="name"
                        rules={[
                            { required: true, message: t('role.form.nameRequired') },
                            {
                                min: ROLE_NAME_MIN_LENGTH,
                                max: ROLE_NAME_MAX_LENGTH,
                                message: t('role.form.nameLength'),
                            },
                            {
                                pattern: ROLE_NAME_PATTERN,
                                message: t('role.form.namePattern'),
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label={t('role.form.description')}
                        name="description"
                        rules={[
                            {
                                validator: async (_, value: string | undefined) => {
                                    if (!value || value.length <= ROLE_DESCRIPTION_MAX_LENGTH) {
                                        return
                                    }
                                    throw new Error(t('role.form.descriptionLength'))
                                },
                            },
                        ]}
                    >
                        <Input.TextArea
                            rows={2}
                            showCount
                        />
                    </Form.Item>
                    {currentRole && (
                        <Form.Item
                            label={t('role.form.status')}
                            name="is_active"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    )}
                </Form>

                {/* 权限选择（嵌入在同一对话框中） */}
                <div style={rolePermissionSectionStyle}>
                    <h4>{t('role.form.permissions')}</h4>
                    {permissionError && (
                        <Alert
                            type="error"
                            showIcon
                            message={permissionError}
                            style={{ marginBottom: 12 }}
                        />
                    )}
                    <div
                        data-testid="role-permission-scroll-panel"
                        style={rolePermissionScrollPanelStyle}
                    >
                        {renderPermissionTree()}
                    </div>
                </div>
            </Modal>

            {/* 权限分配对话框（独立） */}
            <Modal
                title={t('role.modal.permissionTitle')}
                open={permissionModalVisible}
                onCancel={closePermissionModal}
                footer={[
                    <Button key="close" onClick={closePermissionModal}>
                        {t('common:buttons.cancel')}
                    </Button>,
                ]}
                width={500}
                maskClosable={false}
                styles={{ body: roleModalBodyStyle }}
            >
                <div
                    data-testid="role-permission-readonly-scroll-panel"
                    style={rolePermissionScrollPanelStyle}
                >
                    {renderPermissionTree(true)}
                </div>
            </Modal>
        </PageLayout>
    )
}

export default RoleManagement
